import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const DentistNearAledo = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Where can I find a dentist near Aledo TX?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental serves Aledo residents at our convenient location at 4620 Citylake Blvd W, Fort Worth, TX 76132. We're just 15 minutes from Aledo via I-20."
        }
      },
      {
        "@type": "Question",
        "name": "What makes Fort Worth Dental the best choice for Aledo patients?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fort Worth Dental offers services not available in Aledo, including a board-certified implant dentist (Dr. Mund, Diplomate ABOI), LANAP laser gum treatment, and same-day dental implants."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Dentist Near Aledo TX | Top Rated Dental Care | Fort Worth Dental</title>
        <meta name="description" content="Best dentist near Aledo TX. Fort Worth Dental offers dental implants, Invisalign, LANAP laser treatment. Board-certified implant dentist. Just 15 min from Aledo. Call (817) 985-3337." />
        <meta name="keywords" content="dentist near aledo tx, aledo dentist, dental implants aledo, best dentist aledo texas, family dentist near aledo" />
        <link rel="canonical" href="https://fortworthdental.com/dentist-near-aledo" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <p className="text-teal-200 font-semibold mb-2">Proudly Serving Aledo & Parker County</p>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">Top Rated Dentist Near Aledo, TX</h1>
            <p className="text-xl mb-8">Aledo families trust Fort Worth Dental for expert dental care. Board-certified implant dentist, LANAP laser treatment, and comprehensive family dentistry - just 15 minutes away.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:8179853337" className="bg-white text-teal-700 px-8 py-4 rounded-lg font-bold text-center hover:bg-gray-100">Call (817) 985-3337</a>
              <Link to="/contact-us" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-center hover:bg-white/10">Book Online</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Why Aledo Families Choose Fort Worth Dental</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: '🏆', title: 'Board-Certified', desc: 'Dr. Mund is a Diplomate of the American Board of Oral Implantology' },
              { icon: '⚡', title: 'Advanced Technology', desc: 'LANAP laser, digital X-rays, same-day crowns' },
              { icon: '👨‍👩‍👧‍👦', title: 'Family Friendly', desc: 'Comprehensive care for patients of all ages' },
              { icon: '💰', title: 'Affordable Options', desc: 'Insurance accepted, financing available' },
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-lg shadow text-center">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="font-bold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services for Aledo */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">Services Not Available in Aledo</h2>
          <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">Fort Worth Dental offers specialized services that Aledo-area practices don't provide:</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/lanap" className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition border-l-4 border-teal-500">
              <h3 className="font-bold text-gray-800 mb-2">LANAP Laser Gum Treatment</h3>
              <p className="text-gray-600 text-sm">FDA-cleared laser alternative to gum surgery. Faster healing, less pain.</p>
            </Link>
            <Link to="/implant-center/mini-dental-implants" className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition border-l-4 border-teal-500">
              <h3 className="font-bold text-gray-800 mb-2">Mini Dental Implants</h3>
              <p className="text-gray-600 text-sm">Smaller implants for patients with limited bone. Same-day placement.</p>
            </Link>
            <Link to="/dental-implants" className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition border-l-4 border-teal-500">
              <h3 className="font-bold text-gray-800 mb-2">All-on-4 Implants</h3>
              <p className="text-gray-600 text-sm">Full arch restoration with just 4 implants. Transform your smile in one day.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Directions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Easy Drive from Aledo</h2>
              <div className="bg-teal-50 p-6 rounded-lg mb-6">
                <p className="font-bold text-teal-800 mb-2">Directions from Aledo:</p>
                <ol className="list-decimal list-inside text-gray-700 space-y-2">
                  <li>Head east on I-20 toward Fort Worth</li>
                  <li>Take exit 429A for Bryant Irvin Rd</li>
                  <li>Turn left onto Citylake Blvd W</li>
                  <li>Fort Worth Dental is on your right</li>
                </ol>
                <p className="text-teal-600 font-semibold mt-4">Total drive time: ~15 minutes</p>
              </div>
              <div className="bg-gray-100 p-6 rounded-lg">
                <p className="font-bold text-gray-800">Fort Worth Dental</p>
                <p className="text-gray-600">4620 Citylake Blvd W</p>
                <p className="text-gray-600">Fort Worth, TX 76132</p>
                <p className="text-teal-600 font-bold mt-2">(817) 985-3337</p>
              </div>
            </div>
            <div className="bg-gray-200 rounded-lg overflow-hidden h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3357.0!2d-97.438!3d32.728!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDQzJzQxLjAiTiA5N8KwMjYnMTcuNCJX!5e0!3m2!1sen!2sus!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="Fort Worth Dental Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Aledo's Choice for Expert Dental Care</h2>
          <p className="text-xl mb-8">Join hundreds of Aledo families who trust Fort Worth Dental for their smiles.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:8179853337" className="bg-white text-teal-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100">Call (817) 985-3337</a>
            <Link to="/specials" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10">View Specials</Link>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Request Your Appointment</h2>
          <AppointmentForm source="aledo_landing" />
        </div>
      </section>
    </div>
  );
};

export default DentistNearAledo;